import { useCallback, useEffect, useMemo, useState } from "react";
import { usePropertyApiClient } from "../../property/services/usePropertyApiClient";
import { createPartnerNetworkService, getErrorText } from "../services/partnerNetworkService";

export const useNetworkService = () => {
  const apiClient = usePropertyApiClient();
  return useMemo(() => createPartnerNetworkService(apiClient), [apiClient]);
};

/**
 * Loads a whole list and refetches on demand. There is no get-by-id endpoint, so detail and edit pages
 * read their record from this list (see useListRecord).
 */
export const useRecordList = (load, fallbackError) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await load());
      setError(null);
    } catch (err) {
      setItems([]);
      setError(getErrorText(err, fallbackError));
    } finally {
      setLoading(false);
    }
  }, [load, fallbackError]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { items, loading, error, refetch };
};

/**
 * The record with the given id out of a list. `seed` (the record the list page passed along in the router
 * state) renders instantly; a deep link or refresh has no seed and waits for the refetch. Once loaded, a
 * missing id means "not found".
 */
export const useListRecord = (load, id, seed, fallbackError) => {
  const { items, loading, error, refetch } = useRecordList(load, fallbackError);
  const found = items.find((item) => String(item.id) === String(id)) || null;
  const record = found || (loading && seed && String(seed.id) === String(id) ? seed : null);
  return { record, loading: loading && !record, error, notFound: !loading && !error && !found, refetch };
};

/**
 * Every signed-in partner (ROLE_USER, ROLE_FRONT_DESK, ROLE_TEAM_MEMBER, ROLE_LEAD, ROLE_ADMIN) can add, edit
 * and delete. These routes already sit behind PartnerOnlyLayout, and the backend is the real gate: adding a
 * partner needs a PARTNER user type, and edit / delete only work on records the caller owns (or for an admin).
 * The lists only contain the caller's own records, so no extra ownership check is needed here.
 */
export const useNetworkPermissions = () => useMemo(() => ({ canAdd: true, canEdit: true, canDelete: true }), []);
